import { ComponentProps, ComponentType } from 'react';

export enum ViewVariant {
  DrawerBottom,
  DrawerRight,
  Fullscreen,
  Dialog,
}

export type LayoutConfig = {
  mobile?: ViewVariant;
  desktop?: ViewVariant;
};

export interface IModalProps<O = void> {
  /**
   * Action inside modal successfully end
   */
  onSuccess: (result: O) => void;
  /**
   * Modal closed without any action
   */
  onCancel: () => void;
  /**
   * Action inside modal got error.
   * modal may be closed manually
   * @param err
   */
  onFailure: (err: Error) => void;
}

export interface IModalDescription<P extends IModalProps<O>, O = Parameters<P['onSuccess']>[0]> {
  /**
   * Namespace Specific String.
   *
   * Nothing more than modal name in urn terms
   */
  nss: string;
  /**
   * Function returns modal layout
   */
  loader: () => Promise<ComponentType<P>>;
  layout?: LayoutConfig;
}

export type ModalIdentifier = string;

export interface IModalManager {
  /**
   * Open modal
   * @param modal modal description object
   * @param props modal props
   * @param parentModal parent modal identifier
   * @param layout override default layout config
   * @return opened modal identifier
   */
  open<
    M extends IModalDescription<P>,
    P extends IModalProps<Parameters<P['onSuccess']>[0]> = ComponentProps<Awaited<ReturnType<M['loader']>>>,
  >(
    modal: M,
    props: P,
    parentModal?: ModalIdentifier,
    layout?: LayoutConfig,
  ): Promise<ModalIdentifier>;
  close(modal: ModalIdentifier): void;
}

export type ModalStat = Set<{ nss: string; uniqId: ModalIdentifier; open: boolean }>;
